/**
 * VoicePreviewPlayer Component
 *
 * Plays ElevenLabs voice preview audio.
 * Shows play/pause controls with current playback state.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Play, Pause, Volume2, VolumeX, RefreshCw, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IVoicePreviewPlayerProps } from '@/@types/voices';

function VoicePreviewPlayer({
  previewUrl,
  voiceName,
  className,
  onGeneratePreview,
  isGeneratingPreview,
}: IVoicePreviewPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // When previewUrl changes, reset derived playback state during render.
  // Storing the previous URL as state lets us detect the change without a ref or effect.
  const [syncedPreviewUrl, setSyncedPreviewUrl] = useState(previewUrl);
  if (syncedPreviewUrl !== previewUrl) {
    setSyncedPreviewUrl(previewUrl);
    if (isPlaying) setIsPlaying(false);
    if (error !== null) setError(null);
  }

  // Pause and discard the audio element when previewUrl changes (side effect only — no setState).
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [previewUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePlayPause = useCallback(() => {
    if (!previewUrl) {
      setError('No preview available');
      return;
    }

    if (!audioRef.current || audioRef.current.src !== previewUrl) {
      // Create fresh Audio element (or recreate after error/URL change)
      if (audioRef.current) {
        audioRef.current.pause();
      }
      audioRef.current = new Audio(previewUrl);
      audioRef.current.addEventListener('ended', () => setIsPlaying(false));
      audioRef.current.addEventListener('error', () => {
        setError('Failed to load audio');
        setIsPlaying(false);
        // Reset ref so next click creates a fresh Audio
        audioRef.current = null;
      });
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      setError(null);
      audioRef.current.play().catch(() => {
        setError('Failed to play audio');
        setIsPlaying(false);
        // Reset ref so next click retries with a fresh Audio
        audioRef.current = null;
      });
      setIsPlaying(true);
    }
  }, [previewUrl, isPlaying]);

  const hasPreview = Boolean(previewUrl);

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Button
        variant="outline"
        size="sm"
        onClick={handlePlayPause}
        disabled={!hasPreview}
        className="flex items-center gap-1.5"
        title={hasPreview ? `Play preview of ${voiceName}` : 'No preview available'}
      >
        {isPlaying ? (
          <>
            <Pause className="h-3.5 w-3.5" />
            <span className="text-xs">Pause</span>
          </>
        ) : (
          <>
            <Play className="h-3.5 w-3.5" />
            <span className="text-xs">Preview</span>
          </>
        )}
      </Button>

      {!hasPreview && onGeneratePreview && (
        <Button
          variant="outline"
          size="sm"
          onClick={onGeneratePreview}
          disabled={isGeneratingPreview}
          className="flex items-center gap-1.5"
          title="Generate preview audio"
        >
          {isGeneratingPreview ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span className="text-xs">Generating...</span>
            </>
          ) : (
            <>
              <RefreshCw className="h-3.5 w-3.5" />
              <span className="text-xs">Generate</span>
            </>
          )}
        </Button>
      )}

      {!hasPreview && !onGeneratePreview && (
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <VolumeX className="h-3 w-3" />
          No preview
        </span>
      )}

      {isPlaying && (
        <span className="text-xs text-green-400 light:text-green-600 flex items-center gap-1">
          <Volume2 className="h-3 w-3 animate-pulse" />
          Playing
        </span>
      )}

      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
}

export { VoicePreviewPlayer };
